import { useQuery } from '@tanstack/react-query';

import { ReactNode } from 'react';

import { Avatar } from '@mui/material';
import { alpha } from '@mui/material/styles';

import { useApiClient, useHasPermission } from '@@common';

import { FileClient } from '../../api/api';
import { Permissions } from '../../common/Permissions';

export default function AgentAvatar({
	name,
	fileId,
	previewUrl,
	size = 32,
	variant = 'circular',
	cutout = false,
	fallback,
}: {
	name: string;
	fileId?: string | null;
	previewUrl?: string;
	size?: number;
	variant?: 'circular' | 'rounded' | 'square';
	cutout?: boolean;
	fallback?: ReactNode;
}) {
	const client = useApiClient(FileClient);
	const canReadFiles = useHasPermission(Permissions.FileRead);
	const { data } = useQuery({
		queryKey: ['files', 'agent-avatar', fileId],
		queryFn: () => client.getById(fileId!, true),
		enabled: !!fileId && canReadFiles && !previewUrl,
		staleTime: 30 * 60 * 1000,
		refetchInterval: 45 * 60 * 1000,
		retry: false,
	});
	return (
		<Avatar
			variant={cutout ? 'square' : variant}
			src={previewUrl || (canReadFiles ? data?.url : undefined)}
			alt={name || 'Agent avatar'}
			sx={(theme) => ({
				width: size,
				height: size,
				...(!cutout && variant === 'rounded' ? { borderRadius: 2 } : {}),
				bgcolor: cutout ? 'transparent' : 'action.selected',
				color: 'text.secondary',
				'& img': {
					objectFit: cutout ? 'contain' : 'cover',
					objectPosition: cutout ? 'center bottom' : 'center',
					...(cutout ? { filter: `drop-shadow(0 3px 5px ${alpha(theme.palette.text.primary, 0.18)})` } : {}),
				},
			})}
		>
			{fallback ?? (name.trim().charAt(0).toUpperCase() || '?')}
		</Avatar>
	);
}
